"use client"

import { usePathname } from "next/navigation"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

export default function CookiePolicyPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
              Çerez Politikası
            </h1>
            <p className="text-lg text-white/90 sm:text-xl">
              Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-12 lg:pb-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Content */}
            <div className="prose prose-slate max-w-none space-y-8 text-sm sm:text-base">
              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">1. Çerez Nedir?</h2>
                <p>
                  Çerezler (cookies), web sitelerini ziyaret ettiğinizde cihazınıza (bilgisayar, tablet, telefon) kaydedilen 
                  küçük metin dosyalarıdır. Çerezler, web sitesinin düzgün çalışmasını sağlar, kullanıcı deneyimini iyileştirir 
                  ve site performansını analiz etmemize yardımcı olur.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">2. Çerezlerin Kullanım Amacı</h2>
                <p>Web sitemizde çerezler aşağıdaki amaçlarla kullanılmaktadır:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Web sitesinin temel işlevlerinin çalışmasını sağlamak</li>
                  <li>Kullanıcı tercihlerini hatırlamak</li>
                  <li>Site performansını analiz etmek ve iyileştirmek</li>
                  <li>Kullanıcı deneyimini kişiselleştirmek</li>
                  <li>Güvenliği sağlamak</li>
                  <li>Pazarlama ve reklam faaliyetleri (açık rıza ile)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">3. Kullandığımız Çerez Türleri</h2>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3">3.1. Zorunlu Çerezler</h3>
                <p>
                  Bu çerezler, web sitesinin temel işlevlerinin çalışması için zorunludur. Bu çerezler olmadan web sitesi 
                  düzgün çalışmaz. Bu çerezler için kullanıcı rızası alınmaz çünkü web sitesinin çalışması için gereklidir.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">Örnekler:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Oturum yönetimi çerezleri</li>
                    <li>Güvenlik çerezleri</li>
                    <li>Dil tercihi çerezleri</li>
                  </ul>
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3">3.2. Analitik Çerezler</h3>
                <p>
                  Bu çerezler, web sitesinin nasıl kullanıldığını anlamamıza yardımcı olur. Ziyaretçi sayısı, en çok ziyaret 
                  edilen sayfalar, kullanıcı davranışları gibi bilgileri toplar. Bu bilgiler anonimdir ve kişisel kimlik 
                  bilgilerinizi içermez.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">Kullandığımız Analitik Araçlar:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Google Analytics (anonim IP adresi ile)</li>
                    <li>Web sitesi performans analizi araçları</li>
                  </ul>
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3">3.3. Fonksiyonel Çerezler</h3>
                <p>
                  Bu çerezler, web sitesinin gelişmiş özelliklerini ve kişiselleştirmeyi sağlar. Tercihlerinizi hatırlar ve 
                  size daha iyi bir deneyim sunar.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">Örnekler:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Dil tercihi</li>
                    <li>Bölge ayarları</li>
                    <li>Kullanıcı tercihleri</li>
                  </ul>
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3">3.4. Pazarlama Çerezleri</h3>
                <p>
                  Bu çerezler, size daha uygun reklamlar ve içerikler göstermek için kullanılır. Bu çerezler sadece açık 
                  rızanız ile kullanılır.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">Örnekler:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Reklam kişiselleştirme</li>
                    <li>İçerik önerileri</li>
                    <li>Sosyal medya entegrasyonları</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">4. Üçüncü Taraf Çerezler</h2>
                <p>
                  Web sitemizde, hizmet sağlayıcılarımız tarafından yerleştirilen üçüncü taraf çerezler de kullanılmaktadır. 
                  Bu çerezler, analitik, pazarlama ve diğer hizmetler için kullanılır.
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">Üçüncü Taraf Hizmetler:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Google Analytics</li>
                    <li>Sosyal medya platformları (LinkedIn, vb.)</li>
                    <li>Hosting ve altyapı sağlayıcıları</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">5. Çerezlerin Saklanma Süresi</h2>
                <p>Çerezler, kullanım amaçlarına göre farklı sürelerde saklanır:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>
                    <strong>Oturum Çerezleri:</strong> Tarayıcı kapatıldığında silinir
                  </li>
                  <li>
                    <strong>Kalıcı Çerezler:</strong> Belirli bir süre (genellikle 1-2 yıl) cihazınızda kalır
                  </li>
                  <li>
                    <strong>Analitik Çerezler:</strong> Genellikle 2 yıl süreyle saklanır
                  </li>
                  <li>
                    <strong>Pazarlama Çerezleri:</strong> Açık rızanız geri alınana kadar veya belirli bir süre (genellikle 1 yıl)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">6. Çerez Tercihlerinizi Yönetme</h2>
                <p>
                  Çerez tercihlerinizi tarayıcı ayarlarınızdan yönetebilirsiniz. Ancak, bazı çerezlerin devre dışı bırakılması 
                  web sitesinin bazı özelliklerinin çalışmamasına neden olabilir.
                </p>

                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3">6.1. Tarayıcı Ayarları</h3>
                <p>Popüler tarayıcılarda çerez ayarlarını yönetmek için:</p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>
                    <strong>Google Chrome:</strong> Ayarlar → Gizlilik ve güvenlik → Çerezler ve diğer site verileri
                  </li>
                  <li>
                    <strong>Mozilla Firefox:</strong> Seçenekler → Gizlilik ve Güvenlik → Çerezler ve site verileri
                  </li>
                  <li>
                    <strong>Safari:</strong> Tercihler → Gizlilik → Çerezler ve web sitesi verileri
                  </li>
                  <li>
                    <strong>Microsoft Edge:</strong> Ayarlar → Gizlilik, arama ve hizmetler → Çerezler ve site izinleri
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">7. Çerezlerin Devre Dışı Bırakılmasının Etkileri</h2>
                <p>
                  Çerezleri devre dışı bırakırsanız, web sitesinin bazı özellikleri düzgün çalışmayabilir:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Oturum yönetimi sorunları</li>
                  <li>Tercihlerinizin kaydedilmemesi</li>
                  <li>Kişiselleştirilmiş içeriklerin gösterilmemesi</li>
                  <li>Form verilerinin kaybolması</li>
                  <li>Site performansının düşmesi</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">8. Mobil Cihazlarda Çerez Yönetimi</h2>
                <p>
                  Mobil cihazlarda da çerez tercihlerinizi yönetebilirsiniz:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>
                    <strong>iOS (Safari):</strong> Ayarlar → Safari → Gizlilik ve Güvenlik → Çerezleri Engelle
                  </li>
                  <li>
                    <strong>Android (Chrome):</strong> Chrome menü → Ayarlar → Site ayarları → Çerezler
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">9. Çerez Politikası Değişiklikleri</h2>
                <p>
                  Bu Çerez Politikası, yasal düzenlemelerdeki değişiklikler veya web sitesi güncellemeleri nedeniyle 
                  değiştirilebilir. Önemli değişiklikler web sitemizde duyurulacaktır.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 font-mono">10. İletişim</h2>
                <p>
                  Çerez politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm">
                  <p className="font-semibold mb-2">LAK VERSE</p>
                  <p className="text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
      <CTA locale={currentLocale} />
    </>
  )
}
